import React from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, AlertTriangle, Info, X as XIcon } from 'lucide-react';
import {
  QBO_FAULT_CTA,
  QBO_FAULT_TITLE,
  QBO_MAPPING_ROUTE,
  QBO_PUSH_OFF_REASON,
  QBO_RECONNECT_ROUTE,
} from '../../lib/qbo-errors';
import type { QboPushRefusal } from '../../lib/qbo-errors';
import type { QboPushGate } from '../../hooks/useQboReadiness';

/**
 * The QuickBooks push's refusals and its off-switch, rendered ONCE for both
 * push buttons (invoice detail and the invoice list rows), so the two screens
 * cannot drift on copy or — the part that matters — on colour.
 *
 * Colour is part of the message (fe#421/#452: a refusal rendered under a tick):
 *   info     already pushed / being pushed — nothing is wrong. Not red, not the
 *            success toast.
 *   warning  QuickBooks holds a conflicting invoice number. Someone has to act
 *            in QuickBooks; nothing here can retry it.
 *   action   mapping is missing — a route to the tab that fixes it.
 * The tone is on `data-tone` so tests can assert it rather than a class name.
 */

/** The mapping screen's tab names, for the blocker buttons. */
const TAB_LABEL: Record<string, string> = {
  customer: 'Customers',
  rate_sheet_item: 'Rate sheet rows',
  item: 'Line items',
  tax_code: 'Tax codes',
  discount_account: 'Discounts',
};

export function QboPushRefusalNotice({
  refusal,
  onOpenMapping,
  onDismiss,
}: {
  refusal: QboPushRefusal;
  /** Where a mapping link goes. The caller decides how to navigate. */
  onOpenMapping: (kind: string) => void;
  onDismiss: () => void;
}) {
  const dismiss = (
    <button
      type="button"
      onClick={onDismiss}
      aria-label="Dismiss QuickBooks notice"
      className="shrink-0 rounded p-1 text-fieldiq-muted hover:text-fieldiq-accent"
    >
      <XIcon className="w-4 h-4" />
    </button>
  );

  if (refusal.kind === 'already_pushed' || refusal.kind === 'in_progress') {
    const title = refusal.kind === 'already_pushed'
      ? `Already in QuickBooks${refusal.qboInvoiceId ? ` (#${refusal.qboInvoiceId})` : ''}.`
      : 'Being pushed to QuickBooks right now. Check back in a minute.';
    return (
      <div
        data-testid="qbo-push-notice"
        data-tone="info"
        role="status"
        className="flex items-start gap-3 rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-3"
      >
        <Info size={18} className="mt-0.5 shrink-0 text-fi-signal-blue" aria-hidden="true" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-fieldiq-text">{title}</p>
          <p className="text-xs text-fieldiq-muted mt-1">{refusal.message}</p>
        </div>
        {dismiss}
      </div>
    );
  }

  if (refusal.kind === 'docnumber_exists') {
    return (
      <div
        data-testid="qbo-push-notice"
        data-tone="warning"
        role="alert"
        className="flex items-start gap-3 rounded-fi-panel border border-fieldiq-accent/40 bg-fieldiq-accent/10 px-4 py-3"
      >
        <AlertTriangle size={18} className="mt-0.5 shrink-0 text-fieldiq-accent" aria-hidden="true" />
        <div className="flex-1 min-w-0">
          <p className="text-sm text-fieldiq-text">{refusal.message}</p>
          <p className="text-xs text-fieldiq-muted mt-1">
            {refusal.qboInvoiceIds.length === 0
              ? 'QuickBooks invoice ids: —'
              : `QuickBooks invoice ${refusal.qboInvoiceIds.length === 1 ? 'id' : 'ids'}: ${refusal.qboInvoiceIds.map((id) => `#${id}`).join(', ')}`}
          </p>
        </div>
        {dismiss}
      </div>
    );
  }

  // not_ready and mapping_required: both are a route to the mapping screen.
  // The testid is the one the invoice screen's mapping banner always had.
  return (
    <div
      data-testid="qbo-mapping-refusal"
      data-tone="action"
      className="flex items-start gap-3 rounded-fi-panel border border-fieldiq-accent/40 bg-fieldiq-accent/10 p-4"
    >
      {/* The HUE is on the icon and the edge; the sentence is plain ink.
          An accent-coloured paragraph is unreadable on the light card. */}
      <AlertCircle size={18} className="mt-0.5 shrink-0 text-fieldiq-accent" aria-hidden="true" />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-fieldiq-text">{refusal.message}</p>
        {refusal.kind === 'not_ready' ? (
          <ul className="mt-2 space-y-2">
            {refusal.blockers.map((b) => (
              <li key={b.kind} data-testid={`qbo-refusal-blocker-${b.kind}`} className="flex flex-col gap-1 sm:flex-row sm:items-start sm:gap-3">
                <span className="flex-1 text-sm text-fieldiq-text">{b.message}</span>
                <button
                  type="button"
                  onClick={() => onOpenMapping(b.kind)}
                  className="fi-action fi-action--compact fi-action--primary shrink-0"
                >
                  Open {TAB_LABEL[b.kind] ?? 'QuickBooks mapping'}
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <div className="flex items-center gap-3 mt-2">
            <button
              type="button"
              onClick={() => onOpenMapping(refusal.mappingKind)}
              className="fi-action fi-action--compact fi-action--primary"
            >
              Open QuickBooks mapping
            </button>
          </div>
        )}
      </div>
      {dismiss}
    </div>
  );
}

/**
 * Why a Push button is disabled, IN TEXT beside it — a tooltip alone is
 * invisible on touch. Nothing while loading: the button is disabled and the
 * answer is a moment away.
 */
export function QboPushOffReason({ gate, className = '' }: { gate: QboPushGate; className?: string }) {
  if (gate.enabled || gate.reason === 'loading') return null;
  const base = `text-xs text-fieldiq-muted ${className}`;
  if (gate.reason === 'not_ready') {
    return (
      <span data-testid="qbo-push-off-reason" className={base}>
        {QBO_PUSH_OFF_REASON}.{' '}
        <Link to={QBO_MAPPING_ROUTE} className="font-semibold text-fieldiq-accent hover:underline">
          Open QuickBooks mapping
        </Link>
      </span>
    );
  }
  if (gate.reason === 'not_connected') {
    return (
      <span data-testid="qbo-push-off-reason" className={base}>
        {QBO_FAULT_TITLE.not_connected}.{' '}
        <Link to={QBO_RECONNECT_ROUTE} className="font-semibold text-fieldiq-accent hover:underline">
          {QBO_FAULT_CTA.not_connected}
        </Link>
      </span>
    );
  }
  return (
    <span data-testid="qbo-push-off-reason" className={base}>
      Couldn't check whether QuickBooks push is on — push is off until it loads.
    </span>
  );
}
